import { api } from '../api';

/**
 * Lazy Google Maps (Places) loader for address autocomplete.
 *
 * The browser key comes from the public GET /config endpoint (API env), so it can be
 * set/rotated without rebuilding the web image. When no key is configured the loader
 * resolves to `null` and callers simply skip the autocomplete UI (manual entry still
 * works). The Maps JS is injected at runtime — same external-CDN model as Google Fonts.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
type GoogleMaps = any;

declare global {
  interface Window {
    google?: { maps?: GoogleMaps };
    __jalGmapsReady?: () => void;
  }
}

/** Structured address parts we fill into the form from a picked place. */
export interface AddressParts {
  line1: string;
  city: string;
  province: string;
  postalCode: string;
  countryCode: string;
}

let keyPromise: Promise<string | null> | null = null;
let mapsPromise: Promise<GoogleMaps | null> | null = null;

function fetchKey(): Promise<string | null> {
  if (!keyPromise) {
    keyPromise = api.config
      .get()
      .then((r) => r.googleMapsApiKey)
      .catch(() => null);
  }
  return keyPromise;
}

/** Load Google Maps + the Places library once. Resolves to `google.maps` or null. */
export function loadGoogleMaps(): Promise<GoogleMaps | null> {
  if (mapsPromise) return mapsPromise;
  mapsPromise = (async () => {
    const key = await fetchKey();
    if (!key) return null;
    if (!window.google?.maps) {
      await new Promise<void>((resolve, reject) => {
        window.__jalGmapsReady = () => resolve();
        const params = new URLSearchParams({
          key,
          v: 'weekly',
          libraries: 'places',
          loading: 'async',
          callback: '__jalGmapsReady',
        });
        const s = document.createElement('script');
        s.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
        s.async = true;
        s.onerror = () => reject(new Error('Failed to load Google Maps'));
        document.head.appendChild(s);
      });
    }
    const maps = window.google?.maps;
    // Ensure the Places library (PlaceAutocompleteElement) is resolved.
    if (maps?.importLibrary) await maps.importLibrary('places');
    return maps ?? null;
  })().catch(() => null);
  return mapsPromise;
}

/**
 * Map a picked place's address components (new Places shape: { types, longText, shortText })
 * to our structured fields. EU-friendly: line1 = "Route Number".
 */
export function mapAddressComponents(components: any[]): AddressParts {
  const list = Array.isArray(components) ? components : [];
  const find = (type: string) =>
    list.find((c) => (c?.types ?? c?.Types ?? []).includes(type));
  const long = (type: string) => {
    const c = find(type);
    return (c?.longText ?? c?.long_name ?? '') as string;
  };
  const short = (type: string) => {
    const c = find(type);
    return (c?.shortText ?? c?.short_name ?? '') as string;
  };
  const line1 =
    [long('route'), long('street_number')].filter(Boolean).join(' ').trim() ||
    long('premise');
  const city =
    long('locality') || long('postal_town') || long('administrative_area_level_2');
  return {
    line1,
    city,
    province: long('administrative_area_level_1'),
    postalCode: long('postal_code'),
    countryCode: (short('country') || '').toUpperCase(),
  };
}
